import {
  ChangeEventHandler,
  ComponentProps,
  FC,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './styles.module.css';
import { FormProvider, useForm } from 'react-hook-form';
import Select, { Option } from '../../../../components/common/Select';
import {
  dateOutOfRangeErrorMessage,
  max,
  required,
  numeric,
} from '../../../../message';
import { logger } from '../../../../services/logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import FormItem from '../../../../components/common/FormItem';
import Input from '../../../../components/common/Input';
import classNames from 'classnames';
import Button from '../../../../components/common/Button';
import { useGetDriverLicensesQuery } from '../../../../api/driverApi';
import {
  useGetCarCategoriesQuery,
  useGetCarDetailQuery,
  useGetCarQualificationsQuery,
} from '../../../../api/carApi';
import Label from '../../../../components/common/Label';
import { CarsLabels, CarsRequestData } from '../../../../types/car';
import CheckBoxs from '../CheckBoxs';
import { generateCarFormData } from '../../generateCarFormData';
import Modal from '../../../../components/common/Modal';
import InputFixedNum from '../../../../components/common/InputFixedNum';
import DateInput from '../../../../components/common/DateInput';
import { format } from 'date-fns';

export type CarFormDataProviderProps = {
  carId: number | null;
  type: 'create' | 'edit';
  onSubmit: (data: CarsRequestData) => void;
};
export type Props = {
  carId: number | null;
  type: 'create' | 'edit';
  onSubmit: (data: CarsRequestData) => void;
  close: () => void;
};
const context = createContext<CarFormDataProviderProps | null>(null);
export const CarFormDataProvider = context.Provider;

const EditSideBar: FC<{ close: () => void }> = ({ close }) => {
  const CarFormDataProps = useContext(context);

  if (
    (!CarFormDataProps?.carId && CarFormDataProps?.type === 'edit') ||
    !CarFormDataProps
  ) {
    return <></>;
  }
  return <EditCompanyForm {...CarFormDataProps} close={() => close()} />;
};

export type ValueObj = {
  name?: string;
  label: string;
  value: string;
  checked: boolean;
};

export type CheckBoxGroupOnChangeProps = {
  values: ValueObj[];
  selectedValues: string[];
};

const labels: Record<keyof CarsLabels, string> = {
  carCategoryId: '車両カテゴリ',
  carNo: '車両No',
  maxWeight: '積載上限重量(kg)',
  maxVolume: '積載上限重量(㎥)',
  qualificationIds: '資格',
  driverLicenseId: '免許種別',
  isAvailable: '利用',
  unavailableDate: '不可日(開始~終了)',
  unavailableStartDate: '不可日(開始)',
  unavailableEndDate: '不可日(終了)',
};
const setValueOpt = {
  shouldTouch: true,
  shouldDirty: true,
  shouldValidate: true,
};
type TypedFormItemProps = ComponentProps<typeof FormItem<CarsLabels>>;
const TypedFormItem = (props: TypedFormItemProps) => (
  <div className={styles.formItem}>
    <FormItem<CarsLabels> {...props} />
  </div>
);

const EditCompanyForm = ({ carId, type, onSubmit, close }: Props) => {
  const { data: detailCar } = useGetCarDetailQuery(
    {
      carId: carId ?? 0,
    },
    { skip: !carId && carId !== 0 }
  );

  const form = useForm({
    defaultValues: generateCarFormData(),
    mode: 'onBlur',
  });
  const { register, setValue, watch, handleSubmit, getFieldState, trigger } =
    form;
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const formData = detailCar
      ? {
          carCategoryId: detailCar.carCategory.id,
          carNo: detailCar.carNo,
          maxWeight: detailCar.maxWeight,
          maxVolume: detailCar.maxVolume,
          qualificationIds: !detailCar.qualifications
            ? []
            : detailCar.qualifications.map((i) => i.id),
          driverLicenseId: detailCar.driverLicense.id,
          unavailableStartDate: detailCar.unavailableStartDate ?? '',
          unavailableEndDate: detailCar.unavailableEndDate ?? '',
        }
      : generateCarFormData();
    form.reset(formData);
  }, [detailCar]);
  const submitHandler = handleSubmit(onSubmit, (e) => logger.error(e));

  const closeForm = () => close();

  // 車両カテゴリハンドラセットアップ
  const { data: carCategoryTypes = [] } = useGetCarCategoriesQuery();
  const carCategoryTypesOptions: Option[] = carCategoryTypes.map((ct) => ({
    label: ct.carCategory,
    value: `${ct.carCategoryId}`,
  }));
  const onChangeCarCategoryType: ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    setValue('carCategoryId', Number(e.target.value), setValueOpt);
  };
  register('carCategoryId', {
    required: required(labels.carCategoryId),
  });

  // 資格種別ハンドラセットアップ
  const { data: carQualificationTypes } = useGetCarQualificationsQuery();
  const [
    carQualificationCheckBoxGroupValues,
    updateCarQualificationCheckBoxGroupValues,
  ] = useState<ValueObj[]>([]);
  const qualificationIds = watch('qualificationIds');
  useEffect(() => {
    updateCarQualificationCheckBoxGroupValues(
      (carQualificationTypes || []).map((item) => {
        const isIncludeData = qualificationIds.includes(
          item.carQualificationId
        );
        return {
          value: `${item.carQualificationId}`,
          label: item.carQualification,
          checked: isIncludeData,
        };
      })
    );
  }, [carQualificationTypes, qualificationIds]);

  const onChangeWasteItem = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateCarQualificationCheckBoxGroupValues(values);
    setValue(
      'qualificationIds',
      selectedValues.map((v) => Number(v)),
      setValueOpt
    );
  };

  // 免許種別ハンドラセットアップ
  const { data: licenseTypes = [] } = useGetDriverLicensesQuery();
  const licenseTypesOptions: Option[] = licenseTypes.map((ct) => ({
    label: ct.driverLicense,
    value: `${ct.driverLicenseId}`,
  }));
  const onChangelicenseType: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue('driverLicenseId', Number(e.target.value), setValueOpt);
  };
  register('driverLicenseId', {
    required: required(labels.driverLicenseId),
  });

  // 利用ハンドラセットアップ
  const isAvailableOptions: Option[] = [
    {
      value: '1',
      label: '可',
    },
    {
      value: '2',
      label: '不可',
    },
  ];
  const [startDateInitialValue] = useState(
    form.getValues().unavailableStartDate
  );
  const [endDateInitialValue] = useState(form.getValues().unavailableEndDate);
  type isAvailable = '1' | '2';
  // 不可日(開始~終了)ハンドラセットアップ
  const unavailableStartDate = watch('unavailableStartDate');
  const unavailableEndDate = watch('unavailableEndDate');
  useEffect(() => {
    if (detailCar) {
      if (!detailCar.unavailableStartDate && !detailCar.unavailableEndDate) {
        setOpenUnavailableDate('1');
      } else {
        setOpenUnavailableDate('2');
      }
    }
  }, [detailCar]);
  const [openUnavailableDate, setOpenUnavailableDate] =
    useState<isAvailable>('1');
  const onChangeIsAvailableType: ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    switch (e.target.value) {
      case '1':
        setOpenUnavailableDate('1');
        setValue('unavailableStartDate', '', setValueOpt);
        setValue('unavailableEndDate', '', setValueOpt);
        break;
      case '2':
        setOpenUnavailableDate('2');
        setValue('unavailableEndDate', endDateInitialValue, setValueOpt);
        setValue('unavailableStartDate', startDateInitialValue, setValueOpt);
        break;
    }
  };

  const startDateValidation = () => {
    return {
      validate: () => {
        return (
          form.getValues().unavailableStartDate === '' ||
          form.getValues().unavailableEndDate === '' ||
          new Date(form.getValues().unavailableStartDate) <=
            new Date(form.getValues().unavailableEndDate) ||
          dateOutOfRangeErrorMessage
        );
      },
    };
  };

  register('unavailableStartDate', startDateValidation());

  const [editModalOpen, setEditModalOpen] = useState(false);
  const openEditModal = () => setEditModalOpen(true);
  const closeEditModal = () => setEditModalOpen(false);

  const editModalContent = (close: () => void) => (
    <div>
      <h3 className={styles.modalHead}>この車両はすでに配車されています</h3>
      <div className={styles.content}>
        <div className={styles.text}>
          車両情報を更新してよろしいでしょうか？
          <div className={styles.redText}>
            更新すると、配車がリセットされます。
            <br />
            確定後、再計算を行うか手動で調整してください。
          </div>
        </div>
        <div className={styles.modalControls}>
          <div className={styles.modalButton}>
            <Button
              variants={['cancel', 'fit']}
              type="button"
              onClick={() => {
                close();
              }}
            >
              キャンセル
            </Button>
          </div>
          <div className={styles.modalButton}>
            <Button
              variants={['fit']}
              type="submit"
              onClick={() => {
                formRef.current?.requestSubmit();
                close();
              }}
            >
              確定
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const getVariant = (key: keyof CarsRequestData) =>
    getFieldState(key)?.error
      ? ['fit' as const, 'error' as const]
      : ['fit' as const];

  const getDateVariant = () =>
    getFieldState('unavailableStartDate')?.error ||
    getFieldState('unavailableEndDate')?.error
      ? ['error' as const]
      : [];

  return (
    <div className={classNames(styles.sidebar, styles.open)}>
      <h4 className={styles.head}>
        {`車両${type === 'create' ? '登録' : '編集'}`}
        <button onClick={closeForm} className={styles.closer}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </h4>
      <FormProvider {...form}>
        <form onSubmit={submitHandler} className={styles.form} ref={formRef}>
          <TypedFormItem label={labels.carCategoryId} name="carCategoryId">
            <div className={styles.input}>
              <Select
                options={carCategoryTypesOptions}
                value={`${watch('carCategoryId')}`}
                onChange={onChangeCarCategoryType}
                variants={getVariant('carCategoryId')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.carNo} name="carNo">
            <div className={styles.input}>
              <Input
                {...register('carNo', {
                  required: required(labels.carNo),
                  validate: {
                    max: (value) =>
                      value.length <= 255 || max(labels.carNo, 255),
                  },
                })}
                variants={getVariant('carNo')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.maxWeight} name="maxWeight">
            <div className={styles.input}>
              <InputFixedNum
                name={
                  register('maxWeight', {
                    required: numeric(labels.maxWeight),
                  }).name
                }
                variants={getVariant('maxWeight')}
                value={watch('maxWeight')}
                digits={2}
                min={0}
                max={999999.99}
                onChange={(x) => setValue('maxWeight', x, setValueOpt)}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.maxVolume} name="maxVolume">
            <div className={styles.input}>
              <InputFixedNum
                name={
                  register('maxVolume', {
                    required: numeric(labels.maxVolume),
                  }).name
                }
                variants={getVariant('maxVolume')}
                value={watch('maxVolume')}
                digits={2}
                min={0}
                max={999999.99}
                onChange={(x) => setValue('maxVolume', x, setValueOpt)}
              />
            </div>
          </TypedFormItem>
          <div className={styles.qualifications}>
            <Label>資格</Label>
            <CheckBoxs
              values={carQualificationCheckBoxGroupValues}
              onChange={onChangeWasteItem}
            ></CheckBoxs>
          </div>
          <TypedFormItem label={labels.driverLicenseId} name="driverLicenseId">
            <div className={styles.input}>
              <Select
                options={licenseTypesOptions}
                value={`${watch('driverLicenseId')}`}
                onChange={onChangelicenseType}
                variants={getVariant('driverLicenseId')}
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.isAvailable} name="isAvailable">
            <div className={styles.input}>
              <Select
                options={isAvailableOptions}
                value={openUnavailableDate}
                onChange={onChangeIsAvailableType}
                variants={['fit' as const]}
              />
            </div>
          </TypedFormItem>
          <div
            className={classNames(openUnavailableDate === '1' && styles.none)}
          >
            <TypedFormItem
              label={labels.unavailableDate}
              name={['unavailableStartDate', 'unavailableEndDate']}
              noLabelWrap={true}
            >
              <div className={classNames(styles.input, styles.unavailableDate)}>
                <DateInput
                  placeholder="開始日"
                  value={
                    unavailableStartDate
                      ? new Date(unavailableStartDate)
                      : undefined
                  }
                  variants={getDateVariant()}
                  placement="top-start"
                  onChange={(d) => {
                    const s = d ? format(d, 'yyyy-MM-dd') : '';
                    setValue('unavailableStartDate', s, setValueOpt);
                    trigger(['unavailableStartDate', 'unavailableEndDate']);
                  }}
                  onBlur={() => trigger('unavailableStartDate')}
                />
                <DateInput
                  placeholder="終了日"
                  value={
                    unavailableEndDate
                      ? new Date(unavailableEndDate)
                      : undefined
                  }
                  variants={getDateVariant()}
                  placement="top-start"
                  onChange={(d) => {
                    const s = d ? format(d, 'yyyy-MM-dd') : '';
                    setValue('unavailableEndDate', s, setValueOpt);
                    trigger(['unavailableStartDate', 'unavailableEndDate']);
                  }}
                  onBlur={() => trigger('unavailableEndDate')}
                />
              </div>
            </TypedFormItem>
          </div>
          <div className={styles.controls}>
            <div className={styles.controlButton}>
              <Button
                variants={['fit']}
                type="button"
                onClick={() => {
                  const isAllocated =
                    detailCar?.isAllocated &&
                    (form.getValues().unavailableEndDate ||
                      form.getValues().unavailableStartDate);

                  if (isAllocated) {
                    openEditModal();
                  } else {
                    formRef.current?.requestSubmit();
                  }
                }}
              >
                確定
              </Button>
            </div>
            <div className={styles.controlButton}>
              <Button
                variants={['fit', 'cancel']}
                type="button"
                onClick={closeForm}
              >
                キャンセル
              </Button>
            </div>
          </div>
          {editModalOpen && (
            <Modal onRequestClose={closeEditModal}>
              {editModalContent(closeEditModal)}
            </Modal>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default EditSideBar;
